import React, { useMemo } from 'react';
import { formatVisits } from '../utils/formatMetrics';
import './Modal.css';

const DestinationsSummaryModal = ({ isOpen, onClose, destinationHexData, clusterPlaceNames, selectedPostcode }) => {
  // Calculate clustered destinations with place names
  const clusteredDestinations = useMemo(() => {
    if (!destinationHexData || !destinationHexData.destinations) return [];

    // Import h3 for clustering
    const h3 = require('h3-js');

    // Cluster hexes into hex5
    const clusterMap = new window.Map();

    destinationHexData.destinations.forEach(dest => {
      try {
        const parentHex = h3.cellToParent(dest.hex9, 5);

        if (clusterMap.has(parentHex)) {
          clusterMap.set(parentHex, clusterMap.get(parentHex) + dest.total_occurences);
        } else {
          clusterMap.set(parentHex, dest.total_occurences);
        }
      } catch (error) {
        console.warn(`Error clustering hex ${dest.hex9}:`, error);
      }
    });

    // Convert to array, filter, and sort
    const destinations = Array.from(clusterMap.entries())
      .filter(([_, count]) => count >= 10000)
      .map(([hex5, count]) => ({
        hex5,
        place_name: clusterPlaceNames[hex5] || 'Unknown area',
        total_occurences: count
      }))
      .sort((a, b) => b.total_occurences - a.total_occurences);

    return destinations;
  }, [destinationHexData, clusterPlaceNames]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Place Name', 'Visitors'];
    const csvRows = [headers.join(',')];

    clusteredDestinations.forEach(dest => {
      const row = [
        `"${dest.place_name}"`,
        `"${formatVisits(dest.total_occurences)}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const postcodeSafe = selectedPostcode ? selectedPostcode.replace(/[^a-z0-9]/gi, '_') : 'destinations';
    a.download = `destinations_${postcodeSafe}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h2>Clustered Destinations Summary</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {selectedPostcode && (
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#34495e', borderRadius: '4px' }}>
              <p style={{ margin: 0, color: '#ecf0f1' }}>
                Showing regional destinations for people from <strong>{selectedPostcode}</strong>
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#95a5a6', fontSize: '0.85rem' }}>
                Only destinations with 10,000+ visitors are shown. Destinations are clustered at regional level (~150km hexes).
              </p>
            </div>
          )}

          {clusteredDestinations.length === 0 ? (
            <div className="empty-state">
              <p>No clustered destinations found with 10,000+ visitors.</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1rem', color: '#ecf0f1' }}>
                <strong>Total Destinations:</strong> {clusteredDestinations.length}
              </div>

              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#2c3e50', zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #34495e', color: '#ecf0f1' }}>
                        Rank
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #34495e', color: '#ecf0f1' }}>
                        Place Name
                      </th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid #34495e', color: '#ecf0f1' }}>
                        Visitors
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clusteredDestinations.map((dest, index) => (
                      <tr
                        key={dest.hex5}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#34495e' : '#2c3e50',
                          borderBottom: '1px solid #4a5f7f'
                        }}
                      >
                        <td style={{ padding: '0.75rem', color: '#95a5a6' }}>
                          {index + 1}
                        </td>
                        <td style={{ padding: '0.75rem', fontWeight: 'bold', color: '#3498db' }}>
                          {dest.place_name}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: '#ecf0f1' }}>
                          {formatVisits(dest.total_occurences)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleExportCSV}
            disabled={clusteredDestinations.length === 0}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DestinationsSummaryModal;
